import * as THREE from "three";
import type { FrameContext, MovementResolution, MovementResolutionRequest, RuntimeSystem, WorldQuery } from "../core/types";
import type { ArchitectureKind } from "./ArchitectureLibrary";
import { createTerrainGeometry, createWaterGeometry, type ChunkBounds } from "./geometry";
import { JiangnanWorldModel, type SurfaceKind } from "./model";
import { createWaterTownPlacements, type AmbientBoatPlacement, type ArchitecturePlacement } from "./WaterTownPlacement";
import {
  WatersideEcology,
  type EcologyAssetPlacement,
  type WatersideEcologyChunk,
  type WorldVegetationKind,
} from "./WatersideEcology";
import { WorldSpatialIndex, type WorldSpatialRegistration } from "./WorldSpatialIndex";

export type { WorldVegetationKind } from "./WatersideEcology";
export type WorldArchitectureKind = ArchitectureKind;

export interface WorldVisualLease {
  object: THREE.Object3D;
  release(): void;
}

export interface WorldVisualAssets {
  /** Borrowed from the provider and never disposed by WorldSystem. */
  terrainMaterials: readonly THREE.Material[];
  /** Returned objects own only their transform; all GPU resources remain borrowed. */
  acquireStatic(id: WorldVegetationKind, variant: number): Promise<WorldVisualLease> | null;
  acquireArchitecture(id: WorldArchitectureKind): Promise<WorldVisualLease> | null;
}

export interface WorldSystemOptions {
  seed: string;
  version?: string;
  chunkSize?: number;
  loadRadius?: number;
  waterMaterial?: THREE.Material;
  visuals?: WorldVisualAssets;
}

interface LoadedChunk {
  group: THREE.Group;
  terrainGeometry: THREE.BufferGeometry;
  waterGeometry: THREE.BufferGeometry | null;
  detail: "near" | "far";
  leases: Set<WorldVisualLease>;
  spatialRegistrations: Set<WorldSpatialRegistration>;
  boats: AmbientBoat[];
  ecology: WatersideEcologyChunk;
  generation: number;
  alive: boolean;
}

interface AmbientBoat {
  object: THREE.Object3D;
  registration: WorldSpatialRegistration;
  originX: number;
  originY: number;
  originZ: number;
  motion: AmbientBoatPlacement;
}

const DEFAULT_CHUNK_SIZE = 48;

export class WorldSystem implements RuntimeSystem, WorldQuery {
  readonly root = new THREE.Group();
  readonly model: JiangnanWorldModel;
  readonly chunkSize: number;

  private readonly scene: THREE.Scene;
  private loadRadius: number;
  private readonly chunks = new Map<string, LoadedChunk>();
  private readonly interactionTargets = new Set<THREE.Object3D>();
  private readonly terrainMaterials: THREE.Material[];
  private readonly ownsTerrainMaterials: boolean;
  private readonly waterMaterial: THREE.Material;
  private readonly ownsWaterMaterial: boolean;
  private readonly visuals: WorldVisualAssets | undefined;
  private readonly spatial: WorldSpatialIndex;
  private readonly ecology: WatersideEcology;
  private currentChunkX = Number.NaN;
  private currentChunkZ = Number.NaN;
  private nextChunkGeneration = 0;
  private disposed = false;

  constructor(scene: THREE.Scene, options: WorldSystemOptions) {
    this.scene = scene;
    this.model = new JiangnanWorldModel(options.seed, options.version);
    this.spatial = new WorldSpatialIndex({
      getHeight: (x, z) => this.getHeight(x, z),
      getWaterDepth: (x, z) => this.getWaterDepth(x, z),
    });
    this.ecology = new WatersideEcology(this.model);
    this.chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE;
    this.loadRadius = options.loadRadius ?? 2;
    this.visuals = options.visuals;
    if (options.visuals && options.visuals.terrainMaterials.length !== 4) {
      throw new Error("WorldVisualAssets.terrainMaterials must contain stone, earth, grass, and water materials");
    }
    this.ownsTerrainMaterials = options.visuals === undefined;
    this.terrainMaterials = options.visuals ? [...options.visuals.terrainMaterials] : [0x777c70, 0x6f6544, 0x557044, 0x455f54].map(
      (color) => new THREE.MeshStandardMaterial({ color, roughness: 0.96, metalness: 0 }),
    );
    this.ownsWaterMaterial = options.waterMaterial === undefined;
    this.waterMaterial = options.waterMaterial ?? new THREE.MeshPhysicalMaterial({
      color: 0x729797,
      transparent: true,
      opacity: 0.66,
      roughness: 0.24,
      metalness: 0,
      depthWrite: false,
    });
    this.root.name = "jiangnan-world";
    this.scene.add(this.root);
  }

  update(context: FrameContext): void {
    if (this.disposed) return;
    this.ecology.update(context);
    this.updateAmbientBoats(context.elapsed);
    const chunkX = Math.floor(context.camera.position.x / this.chunkSize);
    const chunkZ = Math.floor(context.camera.position.z / this.chunkSize);
    if (chunkX !== this.currentChunkX || chunkZ !== this.currentChunkZ) {
      this.streamAround(chunkX, chunkZ);
    }
  }

  resetAround(position: THREE.Vector3): void {
    if (this.disposed) return;
    this.clearChunks();
    this.currentChunkX = Number.NaN;
    this.currentChunkZ = Number.NaN;
    this.streamAround(Math.floor(position.x / this.chunkSize), Math.floor(position.z / this.chunkSize));
  }

  getHeight(x: number, z: number): number {
    return this.spatial.getSurfaceAt(x, z)?.height ?? this.model.getHeight(x, z);
  }

  getWaterDepth(x: number, z: number): number {
    if (this.spatial.getSurfaceAt(x, z)) return 0;
    return this.model.getWaterDepth(x, z);
  }

  getSurfaceKind(x: number, z: number): SurfaceKind {
    if (this.spatial.getSurfaceAt(x, z)) return "stone";
    return this.model.getSurfaceKind(x, z);
  }

  resolveMovement(request: MovementResolutionRequest): MovementResolution {
    return this.spatial.resolveMovement(request);
  }

  setInteractionCollisionEnabled(object: THREE.Object3D, enabled: boolean): void {
    this.spatial.setInteractionEnabled(object, enabled);
  }

  getWaterHeight(x: number, z: number): number | null {
    return this.model.getPoint(x, z).waterHeight;
  }

  getLoadedChunkCount(): number {
    return this.chunks.size;
  }

  getLoadedChunkKeys(): readonly string[] {
    return [...this.chunks.keys()].sort();
  }

  setLoadRadius(radius: number, position: THREE.Vector3): void {
    const next = Math.max(1, Math.min(3, Math.round(radius)));
    if (next === this.loadRadius) return;
    this.loadRadius = next;
    this.clearChunks();
    this.currentChunkX = Number.NaN;
    this.currentChunkZ = Number.NaN;
    this.resetAround(position);
  }

  getInteractiveObjects(): THREE.Object3D[] {
    return [...this.interactionTargets];
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearChunks();
    this.ecology.dispose();
    this.scene.remove(this.root);
    if (this.ownsTerrainMaterials) for (const material of this.terrainMaterials) material.dispose();
    if (this.ownsWaterMaterial) this.waterMaterial.dispose();
  }

  private streamAround(centerX: number, centerZ: number): void {
    this.currentChunkX = centerX;
    this.currentChunkZ = centerZ;
    const wanted = new Set<string>();

    for (let dz = -this.loadRadius; dz <= this.loadRadius; dz += 1) {
      for (let dx = -this.loadRadius; dx <= this.loadRadius; dx += 1) {
        const chunkX = centerX + dx;
        const chunkZ = centerZ + dz;
        const key = this.chunkKey(chunkX, chunkZ);
        const detail = Math.max(Math.abs(dx), Math.abs(dz)) <= 1 ? "near" : "far";
        wanted.add(key);
        const existing = this.chunks.get(key);
        if (!existing || existing.detail !== detail) {
          if (existing) this.removeChunk(key, existing);
          this.chunks.set(key, this.createChunk(chunkX, chunkZ, detail));
        }
      }
    }

    for (const [key, chunk] of this.chunks) {
      if (wanted.has(key)) continue;
      this.removeChunk(key, chunk);
    }
  }

  private createChunk(chunkX: number, chunkZ: number, detail: "near" | "far"): LoadedChunk {
    const bounds: ChunkBounds = {
      minX: chunkX * this.chunkSize,
      minZ: chunkZ * this.chunkSize,
      size: this.chunkSize,
    };
    const group = new THREE.Group();
    group.name = `chunk-${chunkX}-${chunkZ}-${detail}`;
    const terrainGeometry = createTerrainGeometry(this.model, bounds, detail === "near" ? 24 : 10);
    const terrain = new THREE.Mesh(terrainGeometry, this.terrainMaterials);
    terrain.receiveShadow = true;
    group.add(terrain);

    const waterGeometry = createWaterGeometry(this.model, bounds);
    if (waterGeometry) {
      const water = new THREE.Mesh(waterGeometry, this.waterMaterial);
      water.renderOrder = 2;
      group.add(water);
    }

    const chunk: LoadedChunk = {
      group,
      terrainGeometry,
      waterGeometry,
      detail,
      leases: new Set(),
      spatialRegistrations: new Set(),
      boats: [],
      ecology: this.ecology.createChunk(bounds, chunkX, chunkZ, detail),
      generation: ++this.nextChunkGeneration,
      alive: true,
    };
    group.add(chunk.ecology.group);
    this.addVegetation(chunk, chunk.ecology.assets);
    this.addArchitecture(chunk, bounds, chunkX, chunkZ, detail);
    this.root.add(group);
    return chunk;
  }

  private addVegetation(chunk: LoadedChunk, placements: readonly EcologyAssetPlacement[]): void {
    for (const placement of placements) {
      const pending = this.visuals?.acquireStatic(placement.kind, placement.variant);
      if (pending) void this.attachVisual(chunk, placement, pending);
    }
  }

  private async attachVisual(
    chunk: LoadedChunk,
    placement: EcologyAssetPlacement,
    pending: Promise<WorldVisualLease>,
  ): Promise<void> {
    const generation = chunk.generation;
    let lease: WorldVisualLease;
    try {
      lease = await pending;
    } catch {
      return;
    }
    if (this.disposed || !chunk.alive || chunk.generation !== generation) {
      lease.release();
      return;
    }
    const { object } = lease;
    object.name = `world-visual-${placement.kind}`;
    object.position.set(placement.x, this.model.getHeight(placement.x, placement.z), placement.z);
    object.rotation.y = placement.rotation;
    object.scale.multiply(new THREE.Vector3(...placement.scale));
    chunk.leases.add(lease);
    chunk.group.add(object);
  }

  private addArchitecture(
    chunk: LoadedChunk,
    bounds: ChunkBounds,
    chunkX: number,
    chunkZ: number,
    detail: "near" | "far",
  ): void {
    if (!this.visuals) return;
    for (const placement of createWaterTownPlacements(this.model, bounds, chunkX, chunkZ, detail)) {
      this.queueArchitecture(chunk, placement);
    }
  }

  private queueArchitecture(
    chunk: LoadedChunk,
    placement: ArchitecturePlacement,
  ): void {
    const pending = this.visuals?.acquireArchitecture(placement.kind);
    if (!pending) return;
    void this.attachArchitecture(chunk, placement, pending);
  }

  private async attachArchitecture(
    chunk: LoadedChunk,
    placement: ArchitecturePlacement,
    pending: Promise<WorldVisualLease>,
  ): Promise<void> {
    const generation = chunk.generation;
    let lease: WorldVisualLease;
    try {
      lease = await pending;
    } catch {
      return;
    }
    if (this.disposed || !chunk.alive || chunk.generation !== generation) {
      lease.release();
      return;
    }
    const { kind, x, y, z, rotation, scale } = placement;
    lease.object.name = `world-architecture-${kind}`;
    lease.object.position.set(x, y, z);
    lease.object.rotation.y = rotation;
    lease.object.scale.multiply(new THREE.Vector3(...scale));
    lease.object.traverse((object) => {
      if (object.userData.interaction) this.interactionTargets.add(object);
    });
    chunk.leases.add(lease);
    chunk.group.add(lease.object);
    const prefix = `${chunk.group.name}:${chunk.generation}:${kind}:${chunk.spatialRegistrations.size}`;
    const registration = this.spatial.register(lease.object, prefix);
    chunk.spatialRegistrations.add(registration);
    if (placement.boat) {
      chunk.boats.push({ object: lease.object, registration, originX: x, originY: y, originZ: z, motion: placement.boat });
    }
  }

  private chunkKey(x: number, z: number): string {
    return `${x}:${z}`;
  }

  private removeChunk(key: string, chunk: LoadedChunk): void {
    chunk.alive = false;
    this.root.remove(chunk.group);
    this.ecology.removeChunk(chunk.ecology);
    for (const registration of chunk.spatialRegistrations) this.spatial.unregister(registration);
    chunk.spatialRegistrations.clear();
    chunk.boats = [];
    for (const lease of chunk.leases) {
      lease.object.traverse((object) => this.interactionTargets.delete(object));
      chunk.group.remove(lease.object);
      lease.release();
    }
    chunk.leases.clear();
    chunk.terrainGeometry.dispose();
    chunk.waterGeometry?.dispose();
    chunk.group.traverse((object) => {
      this.interactionTargets.delete(object);
      if (!(object instanceof THREE.Mesh) || object instanceof THREE.InstancedMesh) return;
      if (object.geometry !== chunk.terrainGeometry && object.geometry !== chunk.waterGeometry) object.geometry.dispose();
    });
    this.chunks.delete(key);
  }

  private clearChunks(): void {
    for (const [key, chunk] of [...this.chunks]) this.removeChunk(key, chunk);
    this.spatial.clear();
  }

  private updateAmbientBoats(elapsed: number): void {
    for (const chunk of this.chunks.values()) {
      for (const boat of chunk.boats) {
        const travel = Math.sin(elapsed * boat.motion.speed + boat.motion.phase) * boat.motion.range;
        const direction = Math.cos(elapsed * boat.motion.speed + boat.motion.phase) >= 0 ? 1 : -1;
        boat.object.position.x = boat.originX + (boat.motion.axis === "x" ? travel : 0);
        boat.object.position.z = boat.originZ + (boat.motion.axis === "z" ? travel : 0);
        boat.object.position.y = boat.originY + Math.sin(elapsed * 1.65 + boat.motion.phase) * 0.045;
        boat.object.rotation.y = (boat.motion.axis === "x" ? Math.PI * 0.5 : 0) + (direction < 0 ? Math.PI : 0);
        this.spatial.refresh(boat.registration);
      }
    }
  }
}
